// POI-to-CSV column mapping
export interface POIDataConfig {
  poiId: string
  label: string
  columns: { key: string; label: string; unit: string }[]
  aggregate?: 'avg' | 'sum'
  // 3D text placement on building
  textOffset: [number, number, number] // offset from POI target
  textRotationY: number // Y-axis rotation in radians
}

export const POI_DATA_MAP: POIDataConfig[] = [
  {
    poiId: 'battery-cluster-1',
    label: 'Battery Cluster 1',
    aggregate: 'avg',
    textOffset: [0, 2.5, -2],
    textRotationY: Math.PI,
    columns: [
      { key: 'battery_1_soc', label: 'Bat 1 SOC', unit: '%' },
      { key: 'battery_2_soc', label: 'Bat 2 SOC', unit: '%' },
      { key: 'battery_3_soc', label: 'Bat 3 SOC', unit: '%' },
      { key: 'battery_4_soc', label: 'Bat 4 SOC', unit: '%' },
      { key: 'battery_1_power', label: 'Bat 1 Power', unit: 'kW' },
      { key: 'battery_2_power', label: 'Bat 2 Power', unit: 'kW' },
      { key: 'battery_3_power', label: 'Bat 3 Power', unit: 'kW' },
      { key: 'battery_4_power', label: 'Bat 4 Power', unit: 'kW' },
    ],
  },
  {
    poiId: 'battery-cluster-2',
    label: 'Battery Cluster 2',
    aggregate: 'avg',
    textOffset: [0, 2.5, 2],
    textRotationY: 0,
    columns: [
      { key: 'battery_5_soc', label: 'Bat 5 SOC', unit: '%' },
      { key: 'battery_6_soc', label: 'Bat 6 SOC', unit: '%' },
      { key: 'battery_7_soc', label: 'Bat 7 SOC', unit: '%' },
      { key: 'battery_8_soc', label: 'Bat 8 SOC', unit: '%' },
      { key: 'battery_5_power', label: 'Bat 5 Power', unit: 'kW' },
      { key: 'battery_6_power', label: 'Bat 6 Power', unit: 'kW' },
      { key: 'battery_7_power', label: 'Bat 7 Power', unit: 'kW' },
      { key: 'battery_8_power', label: 'Bat 8 Power', unit: 'kW' },
    ],
  },
  {
    poiId: 'battery-cluster-3',
    label: 'Battery Cluster 3',
    aggregate: 'avg',
    textOffset: [-2, 2.5, 0],
    textRotationY: -Math.PI / 2,
    columns: [
      { key: 'battery_9_soc', label: 'Bat 9 SOC', unit: '%' },
      { key: 'battery_10_soc', label: 'Bat 10 SOC', unit: '%' },
      { key: 'battery_11_soc', label: 'Bat 11 SOC', unit: '%' },
      { key: 'battery_12_soc', label: 'Bat 12 SOC', unit: '%' },
      { key: 'battery_9_power', label: 'Bat 9 Power', unit: 'kW' },
      { key: 'battery_10_power', label: 'Bat 10 Power', unit: 'kW' },
      { key: 'battery_11_power', label: 'Bat 11 Power', unit: 'kW' },
      { key: 'battery_12_power', label: 'Bat 12 Power', unit: 'kW' },
    ],
  },
  {
    poiId: 'idt-1',
    label: 'IDT 1',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [{ key: 'trafo_1_oti', label: 'Temp', unit: '\u00B0C' }],
  },
  {
    poiId: 'idt-2',
    label: 'IDT 2',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [{ key: 'trafo_2_oti', label: 'Temp', unit: '\u00B0C' }],
  },
  {
    poiId: 'idt-3',
    label: 'IDT 3',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [{ key: 'trafo_3_oti', label: 'Temp', unit: '\u00B0C' }],
  },
  {
    poiId: 'ecs-cluster-1',
    label: 'ECS Cluster 1',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [
      { key: 'ess_1_active_power', label: 'Power', unit: 'kW' },
      { key: 'pcs_1_active_power', label: 'PCS 1', unit: 'kW' },
      { key: 'pcs_2_active_power', label: 'PCS 2', unit: 'kW' },
      { key: 'pcs_3_active_power', label: 'PCS 3', unit: 'kW' },
      { key: 'pcs_4_active_power', label: 'PCS 4', unit: 'kW' },
    ],
  },
  {
    poiId: 'ecs-cluster-2',
    label: 'ECS Cluster 2',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [
      { key: 'ess_2_active_power', label: 'Power', unit: 'kW' },
      { key: 'pcs_5_active_power', label: 'PCS 5', unit: 'kW' },
      { key: 'pcs_6_active_power', label: 'PCS 6', unit: 'kW' },
      { key: 'pcs_7_active_power', label: 'PCS 7', unit: 'kW' },
      { key: 'pcs_8_active_power', label: 'PCS 8', unit: 'kW' },
    ],
  },
  {
    poiId: 'ecs-cluster-3',
    label: 'ECS Cluster 3',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [
      { key: 'ess_3_active_power', label: 'Power', unit: 'kW' },
      { key: 'pcs_9_active_power', label: 'PCS 9', unit: 'kW' },
      { key: 'pcs_10_active_power', label: 'PCS 10', unit: 'kW' },
      { key: 'pcs_11_active_power', label: 'PCS 11', unit: 'kW' },
      { key: 'pcs_12_active_power', label: 'PCS 12', unit: 'kW' },
    ],
  },
  {
    poiId: 'control-centre',
    label: 'Control Centre',
    textOffset: [2, 4, 0],
    textRotationY: Math.PI / 2,
    columns: [
      { key: 'ess_active_power', label: 'Power', unit: 'kW' },
      { key: 'schedule_power', label: 'Schedule', unit: 'kW' },
    ],
  },
  {
    poiId: 'aux-cabinet',
    label: 'AUX Cabinet',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [
      { key: 'aux_active_power', label: 'AUX Total', unit: 'kW' },
      { key: 'aux_1_active_power', label: 'AUX 1', unit: 'kW' },
      { key: 'aux_2_active_power', label: 'AUX 2', unit: 'kW' },
    ],
  },
  {
    poiId: 'ems',
    label: 'EMS',
    textOffset: [-2, 2, 0],
    textRotationY: -Math.PI / 2,
    columns: [{ key: 'ess_active_power', label: 'Power', unit: 'kW' }],
  },
]
